import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper, Grid, TextField, MenuItem, Button, Snackbar, Alert } from "@mui/material";
import { useMasterData } from "../../../hooks/useMasterData";
import StudentBranchLinkTable from "./StudentBranchLinkTable";
import StudentBranchLinkForm from "./StudentBranchLinkForm";
import { studentBranchLinkApi, StudentRow, BranchItem } from "./studentBranchLinkApi";

const StudentBranchLinkPage: React.FC = () => {
  const { masters, fetchMasters, fetchSections, fetchAcademicSessions } = useMasterData();
  const [filters, setFilters] = useState({ sessionId: "", classId: "", sectionId: "" });
  const [sections, setSections] = useState<{ id: string; name: string }[]>([]);
  const [rows, setRows] = useState<StudentRow[]>([]);
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [openForm, setOpenForm] = useState(false);
  const [currentStudent, setCurrentStudent] = useState<StudentRow | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, []);

  useEffect(() => {
    if (filters.classId) {
      fetchSections(filters.classId).then((s) => setSections(s));
    } else {
      setSections([]);
      setFilters((p) => ({ ...p, sectionId: "" }));
    }
  }, [filters.classId]);

  const canLoad = useMemo(() => !!filters.sessionId && !!filters.classId && !!filters.sectionId, [filters]);

  const handleLoad = async () => {
    if (!canLoad) {
      setSnackbar({ open: true, message: "Select Academic Session, Class and Section", severity: "error" });
      return;
    }
    try {
      const [students, br] = await Promise.all([
        studentBranchLinkApi.getStudents({ sessionId: filters.sessionId, classId: filters.classId, sectionId: filters.sectionId }),
        studentBranchLinkApi.getBranches(),
      ]);
      setRows(students);
      setBranches(br);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load data", severity: "error" });
    }
  };

  const openLinkForm = (r: StudentRow) => {
    setCurrentStudent(r);
    setOpenForm(true);
  };

  const handleSubmit = async (data: { academicSessionId: string; branchId: string; isPrimaryBranch: boolean; linkReason: string; linkedDate: string }) => {
    if (!currentStudent) return;
    try {
      await studentBranchLinkApi.createLink({
        studentId: currentStudent.studentId,
        branchId: data.branchId,
        academicSessionId: data.academicSessionId,
        isPrimaryBranch: data.isPrimaryBranch,
        linkReason: data.linkReason,
        linkedDate: data.linkedDate,
      });
      setSnackbar({ open: true, message: "Link saved", severity: "success" });
      setOpenForm(false);
      handleLoad();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to save link", severity: "error" });
    }
  };

  const handleEdit = (r: StudentRow) => {
    setCurrentStudent(r);
    setOpenForm(true);
  };

  const handleRemove = async (r: StudentRow) => {
    try {
      setSnackbar({ open: true, message: "Select a link to remove from future iteration", severity: "info" });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to remove link", severity: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ fontSize: 22, fontWeight: 600, mb: 2 }}>Student Branch Link</Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Academic Session" value={filters.sessionId} onChange={(e) => setFilters({ ...filters, sessionId: e.target.value })}>
              {masters.academicSessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Class" value={filters.classId} onChange={(e) => setFilters({ ...filters, classId: e.target.value })}>
              {masters.classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Section"
              value={filters.sectionId}
              disabled={!filters.classId}
              onChange={(e) => setFilters({ ...filters, sectionId: e.target.value })}
            >
              {sections.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button fullWidth variant="contained" onClick={handleLoad}>
              Load Students
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <StudentBranchLinkTable rows={rows} onLink={openLinkForm} onEdit={handleEdit} onRemove={handleRemove} />

      {currentStudent && (
        <StudentBranchLinkForm
          open={openForm}
          onClose={() => setOpenForm(false)}
          branches={branches}
          initial={{
            studentName: currentStudent.studentName,
            academicSessionId: filters.sessionId,
            isPrimaryBranch: false,
          }}
          onSubmit={handleSubmit}
        />
      )}

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentBranchLinkPage;
